import { createBrowserRouter, createMemoryRouter, type RouteObject } from "react-router";
import { CrashBoundary } from "@/features/system/CrashBoundary";
import { FeatureUnavailable } from "./FeatureUnavailable";
import { isEnabled } from "./flags";
import { AppLayout } from "./layouts/AppLayout";
import { AuthLayout } from "./layouts/AuthLayout";
import { ReportLayout } from "./layouts/ReportLayout";
import { SharedLayout, SharedReportLayout } from "./layouts/SharedLayout";
import { LiveShellData } from "./LiveShellData";
import { ROUTES, type LayoutKind, type RouteDef } from "./routes";

export interface RouterOptions {
  initialEntries?: string[];
  guards?: boolean;
  wrap?: (route: RouteDef, element: React.ReactNode) => React.ReactNode;
}

function toRouteObject(route: RouteDef, opts: RouterOptions): RouteObject {
  return {
    id: route.id,
    path: route.path,
    lazy: async () => {
      if (route.flag && !isEnabled(route.flag)) return { Component: FeatureUnavailable };
      const Page = (await route.load()).default;
      if (!opts.wrap || opts.guards === false) return { Component: Page };
      const wrap = opts.wrap;
      return { Component: () => <>{wrap(route, <Page />)}</> };
    },
  };
}

const LAYOUTS: Record<LayoutKind, React.ComponentType> = {
  app: AppLayout,
  auth: AuthLayout,
  report: ReportLayout,
  shared: SharedLayout,
  sharedReport: SharedReportLayout,
};

export function buildRouteObjects(opts: RouterOptions = {}): RouteObject[] {
  return (Object.keys(LAYOUTS) as LayoutKind[]).map((kind) => {
    const Layout = LAYOUTS[kind];
    return {
      id: `layout-${kind}`,
      element:
        opts.guards === false ? (
          <Layout />
        ) : (
          <LiveShellData>
            <Layout />
          </LiveShellData>
        ),
      errorElement: <CrashBoundary />,
      hydrateFallbackElement: <></>,
      children: ROUTES.filter((r) => r.layout === kind).map((r) => toRouteObject(r, opts)),
    };
  });
}

export function createAppRouter(opts: RouterOptions = {}) {
  const routes = buildRouteObjects(opts);
  if (opts.initialEntries) return createMemoryRouter(routes, { initialEntries: opts.initialEntries });
  return createBrowserRouter(routes);
}
